import { useEffect, useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { F0ActionItem, F0Icon } from '@factorialco/f0-react'
import {
  CheckCircle,
  ChevronRight,
  FileFilled,
  Search,
  Warning,
} from '@factorialco/f0-react/icons/app'
import { One } from '@factorialco/f0-react/icons/special'
import {
  CreateRecommendationResultDocument,
  GetRecommendationsEnabledDocument,
  UpdateRecommendationResultDocument,
} from 'generated/resources/graphql'

import TextArea from 'modules/core/components/TextArea'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface RecommendationSystemProps {
  expenseId: string
}

interface Flag {
  title: string
  description: string
}

interface RecommendationResult {
  flags: Flag[]
  relevantPolicies: string[]
}

const RecommendationSystem = ({ expenseId }: RecommendationSystemProps) => {
  const { company } = useCurrentSession()
  const companyId = company?.id
  const [isLoading, setIsLoading] = useState(false)
  const [recommendation, setRecommendation] = useState<RecommendationResult | null>(null)
  const [recommendationId, setRecommendationId] = useState<string | null>(null)
  const [humanComment, setHumanComment] = useState('')
  const [showFeedback, setShowFeedback] = useState(true)
  const [hasStarted, setHasStarted] = useState(false)
  const [showSources, setShowSources] = useState(false)

  // Query to check if recommendations are enabled
  const { data, loading: queryLoading } = useQuery(GetRecommendationsEnabledDocument)

  // Mutation to create a recommendation result
  const [createRecommendation, { loading: mutationLoading }] = useMutation(
    CreateRecommendationResultDocument,
    {
      extractorFn: (data) => data.expensesMutations.createExpensesRecommendationResult,
    }
  )

  // Mutation to update a recommendation result
  const [updateRecommendation, { loading: updateLoading }] = useMutation(
    UpdateRecommendationResultDocument,
    {
      extractorFn: (data) => data.expensesMutations.updateExpensesRecommendationResult,
      onCompleted: (response) => {
        const result = response?.expensesMutations?.updateExpensesRecommendationResult
        if (result?.recommendationResult) {
          app.setFlash({
            type: 'correct',
            message: 'Feedback submitted',
          })
        }
      },
    }
  )

  const enabled = data?.permissions?.contextlessPolicyResolutions?.[0]?.can ?? false

  const handlePerformRecommendation = async () => {
    if (!companyId) return

    setIsLoading(true)
    setHasStarted(true)
    try {
      const result = await createRecommendation({
        variables: {
          companyId,
          expenseId,
        },
      })

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
          time: 3000,
        })
      })

      if (handled.success) {
        const recommendationResult = handled.data?.recommendationResult
        if (recommendationResult?.aiResponse) {
          const { flags, relevantPolicies } = recommendationResult.aiResponse
          setRecommendation({
            flags: flags || [],
            relevantPolicies: relevantPolicies || [],
          })
          setRecommendationId(recommendationResult.id)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Automatically trigger recommendation on mount
  useEffect(() => {
    if (enabled && companyId && !hasStarted && !recommendation) {
      handlePerformRecommendation()
    }
  }, [enabled, companyId, hasStarted, recommendation])

  const handleFeedback = async (validation: 'good' | 'bad') => {
    if (!recommendationId) return

    // Track feedback event
    if (validation === 'good') {
      track(trackingEvents.expenses.aiPolicyCheckFeedbackHelpful)
    } else {
      track(trackingEvents.expenses.aiPolicyCheckFeedbackNotHelpful)
    }

    try {
      const result = await updateRecommendation({
        variables: {
          id: recommendationId,
          humanValidation: validation,
          humanComment: humanComment || null,
        },
      })

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
          time: 3000,
        })
      })

      if (handled.success) {
        setShowFeedback(false)
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
        time: 3000,
      })
    }
  }

  // Don't render if loading query or not enabled
  if (queryLoading || !enabled) {
    return null
  }

  const isProcessing = isLoading || mutationLoading
  const isFeedbackProcessing = updateLoading

  return (
    <Box padding="s16" gap="s16" background="white">
      {/* Header Section */}
      <Box gap="s4" flexDirection="row" alignItems="center">
        {isProcessing && !recommendation ? (
          <F0ActionItem title="Checking your company's policy..." status={'executing'} />
        ) : (
          <>
            <div className="-mt-[2px] *:block">
              <F0Icon size={'md'} className="text-f1-icon" icon={One} />
            </div>
            <p className="text-pretty">Reviewed by One</p>
          </>
        )}
      </Box>

      {/* Recommendation Result */}
      {recommendation && (
        <Box width="full" gap="s12">
          <Box
            padding="s20"
            background="neutralBackgroundSubtle"
            borderRadius={{ all: 'abs016' }}
            gap="s12"
          >
            {recommendation.flags.length === 0 ? (
              <Box gap="s8">
                <Box flexDirection="row" alignItems="center" gap="s4">
                  <Text.Normal weight="bold" color="grey1000">
                    This expense meets all policy requirements
                  </Text.Normal>
                  <Box style={{ color: 'green' }}>
                    <F0Icon icon={CheckCircle} size="md" />
                  </Box>
                </Box>
                <Text.Normal color="grey700">
                  The expense is within allowed limits and the receipt is valid
                </Text.Normal>
              </Box>
            ) : (
              <Box gap="s12">
                <Box gap="s12">
                  {recommendation.flags.map((flag, index) => (
                    <Box key={index} gap="s4">
                      <Box flexDirection="row" alignItems="center" gap="s4">
                        <Text.Normal weight="bold" color="grey1000">
                          {flag.title}
                        </Text.Normal>
                        <Box style={{ color: '#ff8c00' }}>
                          <F0Icon icon={Warning} size="md" />
                        </Box>
                      </Box>
                      <Text.Normal color="grey700">{flag.description}</Text.Normal>
                    </Box>
                  ))}
                </Box>
                {recommendation.relevantPolicies.length > 0 && (
                  <Box gap="s8">
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      gap="s4"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowSources(!showSources)}
                    >
                      <Box style={{ color: '#475467' }}>
                        <F0Icon icon={Search} size="lg" />
                      </Box>
                      <Text.Normal color="grey700" weight="medium">
                        Sources
                      </Text.Normal>
                      <Box
                        style={{
                          transition: 'transform 0.2s ease-in-out',
                          transform: showSources ? 'rotate(90deg)' : 'rotate(0deg)',
                          color: '#475467',
                        }}
                      >
                        <F0Icon icon={ChevronRight} size="lg" />
                      </Box>
                    </Box>
                    {showSources && (
                      <Box
                        paddingX="s16"
                        paddingY="s12"
                        borderRadius={{ all: 'abs016' }}
                        gap="s16"
                        style={{ border: '1px solid #E5E7EB' }}
                      >
                        {recommendation.relevantPolicies.map((policy, index) => (
                          <Box key={index} flexDirection="row" alignItems="center" gap="s8">
                            <Box style={{ color: '#6B7280' }}>
                              <F0Icon icon={FileFilled} size="lg" />
                            </Box>
                            <Text.Normal color="grey1000">{policy}</Text.Normal>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Feedback Section */}
          {showFeedback && (
            <Box width="full" gap="s12" padding="s16" background="white">
              <Text.Normal color="grey700" weight="medium">
                WAS THIS HELPFUL?
              </Text.Normal>
              <TextArea
                value={humanComment}
                onChange={(value) => setHumanComment(value)}
                placeholder="Optional: Add feedback to help improve recommendations"
              />
              <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
                <Button
                  type="secondary"
                  onClick={() => handleFeedback('bad')}
                  disabled={isFeedbackProcessing}
                  loading={isFeedbackProcessing}
                  iconName="thumbs-down"
                >
                  Not helpful
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleFeedback('good')}
                  disabled={isFeedbackProcessing}
                  loading={isFeedbackProcessing}
                  iconName="thumbs-up"
                >
                  Helpful
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default RecommendationSystem
